import React, { useEffect, useRef, useState } from 'react';
import { TextField, CircularProgress, Box, Typography } from '@mui/material';
import GoogleMapsService from '../../services/googleMapsService';

interface PlaceResult {
  address: string;
  latitude: number;
  longitude: number;
  formatted_address: string;
}

interface GooglePlacesAutocompleteProps {
  onPlaceSelect: (place: PlaceResult) => void;
  label?: string;
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  error?: boolean;
  helperText?: string;
  sx?: any;
  disabled?: boolean;
}

const GooglePlacesAutocomplete: React.FC<GooglePlacesAutocompleteProps> = ({
  onPlaceSelect,
  label = "",
  placeholder = "Search for a location...",
  value = "",
  onChange,
  onBlur,
  error = false,
  helperText,
  sx,
  disabled = false
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const loadGoogleMaps = async () => {
      try {
        setIsLoading(true);
        
        const googleMapsService = GoogleMapsService.getInstance();
        await googleMapsService.loadGoogleMaps();
        setIsGoogleLoaded(true);
      } catch (error) {
        console.error('Error loading Google Maps:', error);
        setLoadError('Failed to load Google Maps. Please check your API key and internet connection.');
      } finally {
        setIsLoading(false);
      }
    };

    loadGoogleMaps();
  }, []);

  useEffect(() => {
    if (isGoogleLoaded && inputRef.current && !autocompleteRef.current) {
      try {
        // Use the legacy Autocomplete API which works better with TextField
        // The new PlaceAutocompleteElement doesn't expose its value properly
        autocompleteRef.current = new google.maps.places.Autocomplete(inputRef.current, {
          types: ['establishment', 'geocode'],
          fields: ['place_id', 'geometry', 'name', 'formatted_address', 'address_components']
        });

        // Add place changed listener for legacy API
        autocompleteRef.current.addListener('place_changed', () => {
          const place = autocompleteRef.current?.getPlace();
          
          if (place && place.geometry && place.geometry.location) {
            const placeResult: PlaceResult = {
              address: place.name || place.formatted_address || '',
              latitude: place.geometry.location.lat(),
              longitude: place.geometry.location.lng(),
              formatted_address: place.formatted_address || ''
            };
            
            const selectedAddress = place.formatted_address || place.name || '';
            
            setInputValue(selectedAddress);
            onPlaceSelect(placeResult);
            
            if (onChange) {
              onChange(selectedAddress);
            }
          } else if (place && !place.geometry) {
            // Handle case where place has no geometry (partial selection)
            console.warn('⚠️ Selected place has no geometry information:', {
              place_name: place.name,
              place_id: place.place_id,
              formatted_address: place.formatted_address
            });
          } else {
            console.warn('⚠️ No place data received from Google Places');
          }
        });
        
        // Note: We're using the legacy API which works with the TextField directly
        // The TextField's onChange will handle manual typing
      } catch (error) {
        console.error('Error setting up Google Places Autocomplete:', error);
        setLoadError('Failed to initialize Google Places Autocomplete');
      }
    }

    return () => {
      if (autocompleteRef.current) {
        // Legacy API cleanup
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, [isGoogleLoaded, onPlaceSelect, onChange]);

  // Add global styles for the dropdown (legacy API)
  useEffect(() => {
    if (isGoogleLoaded && !document.getElementById('google-places-dark-theme')) {
      const style = document.createElement('style');
      style.id = 'google-places-dark-theme';
      style.textContent = `
        .pac-container {
          background-color: #232933 !important;
          border: 1px solid rgba(255, 255, 255, 0.23) !important;
          border-radius: 4px !important;
          box-shadow: 0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12) !important;
        }
        .pac-item {
          background-color: #232933 !important;
          color: #fff !important;
          border-top: 1px solid rgba(255, 255, 255, 0.1) !important;
          padding: 8px 12px !important;
        }
        .pac-item:hover {
          background-color: #377dff !important;
          color: #fff !important;
        }
        .pac-item-selected {
          background-color: #377dff !important;
          color: #fff !important;
        }
        .pac-item-query {
          color: #fff !important;
        }
        .pac-matched {
          color: #377dff !important;
          font-weight: 600 !important;
        }
        .pac-icon {
          filter: invert(1) !important;
        }
      `;
      document.head.appendChild(style);
    }
  }, [isGoogleLoaded]);

  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value);
    }
  }, [value]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setInputValue(newValue);
    
    if (onChange) {
      onChange(newValue);
    }
  };

  if (loadError) {
    return (
      <Box sx={sx}>
        <TextField
          fullWidth
          inputRef={inputRef}
          label={label}
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={onBlur}
          error={error}
          helperText={loadError + ' You can still enter the address manually.'}
          disabled={disabled}
          sx={{ 
            input: { color: '#fff' }, 
            label: { color: '#fff' },
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#1a202c',
            }
          }}
        />
      </Box>
    );
  }

  return (
    <Box sx={sx}>
      <Box ref={containerRef} sx={{ position: 'relative' }}>
        <TextField
          fullWidth
          inputRef={inputRef}
          label={label}
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={onBlur}
          error={error}
          helperText={helperText}
          disabled={disabled || isLoading || !isGoogleLoaded}
          sx={{ 
            input: { color: '#fff' }, 
            label: { color: '#fff' },
            '& gmp-place-autocomplete': {
              width: '100%',
              fontFamily: 'inherit'
            }
          }}
        />
      </Box>
      {isLoading && (
        <Typography variant="caption" sx={{ color: '#666', mt: 0.5, display: 'block' }}>
          Loading Google Maps...
        </Typography>
      )}
    </Box>
  );
};

export default GooglePlacesAutocomplete;
